/**
 * Inventory filter row — status / capability / type pickers.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import type {
  InventoryStatusFilter,
  InventoryCapabilityFilter,
  InventoryTypeFilter,
} from '../../stores/useNumberInventoryStore';

interface FiltersValue {
  status: InventoryStatusFilter;
  capability: InventoryCapabilityFilter;
  type: InventoryTypeFilter;
}

interface Props {
  filters: FiltersValue;
  onChange: <K extends keyof FiltersValue>(key: K, value: FiltersValue[K]) => void;
}

export function InventoryFilters({ filters, onChange }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={filters.status}
        onValueChange={(v) => onChange('status', v as InventoryStatusFilter)}
      >
        <SelectTrigger className="h-8 w-[150px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="unassigned">Unassigned</SelectItem>
          <SelectItem value="dedicated">Dedicated</SelectItem>
          <SelectItem value="shared">Shared</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.capability ?? 'any'}
        onValueChange={(v) =>
          onChange(
            'capability',
            v === 'any' ? undefined : (v as Exclude<InventoryCapabilityFilter, undefined>),
          )
        }
      >
        <SelectTrigger className="h-8 w-[150px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any capability</SelectItem>
          <SelectItem value="sms">SMS</SelectItem>
          <SelectItem value="mms">MMS</SelectItem>
          <SelectItem value="voice">Voice</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.type ?? 'any'}
        onValueChange={(v) =>
          onChange(
            'type',
            v === 'any' ? undefined : (v as Exclude<InventoryTypeFilter, undefined>),
          )
        }
      >
        <SelectTrigger className="h-8 w-[150px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any type</SelectItem>
          <SelectItem value="local">Local</SelectItem>
          <SelectItem value="tollfree">Toll-free</SelectItem>
          <SelectItem value="mobile">Mobile</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
